import { Component } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { ConfirmationService, MenuItem, MessageService } from 'primeng/api';
import { debounceTime, Subject } from 'rxjs';
import { ItemService } from '../../services/item.service';
import { BulkItemUpdateRequest, ChangedItem, Item, ItemEdit, MassiveItemEdit } from '../../domain/inventory.model';
import { MessageHomeService } from '../../services/message-home.service';

@Component({
    selector: 'app-edit-article',
    templateUrl: './edit-article.component.html',
    styleUrl: './edit-article.component.scss',
    providers: [ConfirmationService],
    standalone: false
})
export class EditArticleComponent {
  private qrSearchSubject = new Subject<string>();
  warehouses = [
    {
      id: 1,
      name: 'Bodega Zapopan'
    }
  ];
  selectedWarehouse: string | null = null;
  manualQrCode: string = '';
  scannedArticles: ChangedItem[] = [];
  showEditDialog = false;
  showMassiveEditDialog = false;
  massiveEditData: MassiveItemEdit = {
    description: '',
    notes: ''
  };
  articleForm!: FormGroup;
  massiveArticleForm!: FormGroup;
  submitted = false;
  massiveEdit: boolean = false;

  private menuItemsCache = new Map<string, MenuItem[]>();
  _isPatching: boolean = true;

  constructor(private fb: FormBuilder, private messageService: MessageService, private router: Router,
    private itemService: ItemService, private confirmationService: ConfirmationService, private messageHomeService: MessageHomeService) {
    this.selectedWarehouse = this.warehouses[0].name;
    this.qrSearchSubject.pipe(
      debounceTime(1000)
    ).subscribe(result => {
      this.onScanSuccess(result.trim());
      this.manualQrCode = '';
    });
  }

  ngOnInit() {
    this.articleForm = this.fb.group({
      itemId: [''],
      code: [''],
      serialNumber: [''],
      description: ['', [Validators.required, Validators.pattern(/\S+/)]],
      quantity: [1, [Validators.required, Validators.min(1)]],
      notes: [{ value: '', disabled: true }]
    });

    this.massiveArticleForm = this.fb.group({
      serialNumber: [''],
      description: ['', [Validators.pattern(/\S+/)]],
      notes: ['', [Validators.pattern(/\S+/)]],
    });

    this.articleForm.valueChanges.subscribe(() => {
      const notes = this.articleForm.get('notes');

      if (this._isPatching) return; // ignora cambios programáticos
      if (notes?.disabled) {
        notes.enable({ emitEvent: false });
        notes.setValidators([Validators.required]);
        notes.updateValueAndValidity({ emitEvent: false });
      }
    });
  }

  playSound() {
    const audio = new Audio();
    audio.src = '../../assets/sounds/beepSound2.mp3';
    audio.load();
    audio.play();
  }

  onScanSuccess(qrCode: string) {
    this.manualQrCode = '';
    this.playSound();

    const existingItem = this.scannedArticles.find(row => row.item.code === qrCode);
    if (existingItem) {
      //this.messageService.add({ severity: 'warn', summary: 'Código duplicado', detail: 'Este código ya esta en la lista' });
      this.editRow(existingItem);
      return;
    }

    this.itemService.validateEdit(qrCode).subscribe(item => {
      this.verifyUpdateType(item);
    }, error => {
      this.messageService.add({ severity: 'error', summary: 'Error', detail: error.error.message });
    });
  }

  verifyUpdateType(item: Item) {
    if (this.massiveEdit) {
      const mergedItem = {
        ...item,
        ...this.massiveEditData
      };
      this.pushArticles(mergedItem, this.massiveEditData);
    } else {
      this.updateArticleToEdit(item);
    }
  }

  updateArticleToEdit(item: ItemEdit) {
    if (item) {
      this._isPatching = true;
      this.articleForm.reset();
      this.articleForm.patchValue(item);
      this.articleForm.markAsPristine();
      this.articleForm.get('notes')?.disable({ emitEvent: false });
      this._isPatching = false;
      this.showEditDialog = true;
    }
  }

  updateMassiveData() {
    this.massiveArticleForm.reset();
    this.massiveArticleForm.patchValue(this.massiveEditData);
    this.showMassiveEditDialog = true;
  }

  onManualInputChange() {
    this.qrSearchSubject.next(this.manualQrCode);
  }

  onUpdateArticles() {
    this.confirmationService.confirm({
      header: 'Confirmación',
      message: `Estás a punto de actualizar ${this.scannedArticles.length} artículos. ¿Seguro que deseas continuar?`,
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Sí',
      rejectLabel: 'No',
      accept: () => {
        this.bulkUpdate();
      }
    });
  }

  bulkUpdate() {
    const request: BulkItemUpdateRequest = {
      items: this.scannedArticles.map(ci => ci.item)
    };
    this.itemService.bulkUpdate(request).subscribe(() => {
      this.messageHomeService.showSuccessAndNavigate(
        this.router,
        '/mobile/menu',
        'Artículo(s) Actualizados',
        'Los Artículo(s) fueron actualizados correctamente'
      );
    }, error => {
      this.messageService.add({ severity: 'error', summary: 'Error', detail: error.error.message });
    });
  }

  onClose() {
    if (!this.scannedArticles.length) {
      this.router.navigate(['/mobile/menu']);
    }
    this.confirmationService.confirm({
      header: 'Confirmación',
      message: '¿Deseas cancelar la edición? Los artículos agregados se perderán.',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Sí',
      rejectLabel: 'No',
      accept: () => {
        this.router.navigate(['/mobile/menu']);
      }
    });
  }

  onCloseEdit() {
    this.articleForm.reset();
    this.showEditDialog = false;
  }

  onCloseMassiveEdit() {
    this.massiveArticleForm.reset();
    this.showMassiveEditDialog = false;
    this.massiveEdit = false;
  }

  onSaveArticle() {
    const modifiedFields = this.getModifiedFields();
    //Verify if item already exists on ScannedArticles
    this.pushArticles(this.articleForm.getRawValue(), modifiedFields);
    this.menuItemsCache.clear();
    this.articleForm.reset();
    this.showEditDialog = false;
  }

  pushArticles(item: Item, modifiedFields: any) {
    //Remove note
    delete modifiedFields.notes;
    const existing = this.scannedArticles.find(
      x => x.item.itemId === item.itemId
    );

    if (existing) {
      // merge changes
      existing.changes = {
        ...existing.changes,
        ...modifiedFields
      };
      existing.item = item;
    } else {
      // New Item in ScannedArticles
      this.scannedArticles.push({
        item: item,
        changes: modifiedFields
      });
    }
  }

  getModifiedFields() {
    const modified: any = {};
    Object.keys(this.articleForm.controls).forEach(key => {
      const control = this.articleForm.get(key);
      if (control?.dirty) {
        modified[key] = control.value;
      }
    });
    return modified;
  }

  onMassiveEditChange(event: any) {
    if (event.checked) {
      this._isPatching = true;
      this.massiveArticleForm.reset();
      this.showMassiveEditDialog = true;
      this._isPatching = false;
    }
  }

  getFieldLabel(key: string): string {
    return this.itemService.formatFieldName(key);
  }

  getFieldDisplayValue(_key: string, value: any): string {
    return this.itemService.formatFieldValue(value);
  }

  getRowMenuItems(row: any): MenuItem[] {
    const key = row.item.itemId;
    if (!this.menuItemsCache.has(key)) {
      this.menuItemsCache.set(key, [
        { label: 'Editar', icon: 'pi pi-pencil', command: () => this.editRow(row) },
        { label: 'Eliminar', icon: 'pi pi-trash', command: () => this.removeRow(row) }
      ]);
    }
    return this.menuItemsCache.get(key)!;
  }

  editRow(row: ChangedItem) {
    this.updateArticleToEdit(row?.item);
  }

  removeRow(row: ChangedItem) {
    this.confirmationService.confirm({
      header: 'Confirmación',
      message: '¿Seguro que deseas <b>quitar este artículo</b> de la lista?',
      icon: 'pi pi-exclamation-triangle',

      acceptLabel: 'Sí',
      rejectLabel: 'No',

      accept: () => {
        this.scannedArticles = this.scannedArticles.filter(
          x => x.item.itemId !== row.item.itemId
        );
        this.menuItemsCache.clear();
      }
    });
  }

  onSaveMassiveArticle() {
    this.massiveEditData = this.massiveArticleForm.value;
    this.massiveArticleForm.reset();
    this.showMassiveEditDialog = false;
  }

  get canSaveMassiveEdit(): boolean {
    const { serialNumber, description, notes } = this.massiveArticleForm.value;
    const hasSerialOrDesc = !!serialNumber?.trim() || !!description?.trim();
    const hasNotes = !!notes?.trim();
    return hasSerialOrDesc && hasNotes;
  }

}
